'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Syndicate', href: '/syndicate' },
  { label: 'About Us', href: '/about' },
  { label: 'Contact Us', href: '/contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'glass-nav py-3' : 'py-5 bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <Link
            href="/"
            className="flex items-center gap-2.5 group"
            aria-label="Aregn Capital home"
          >
            <AppLogo size={32} onClick={() => {}} />
            <span className="font-sans text-base font-semibold tracking-widest uppercase text-foreground hidden sm:block">
              Aregn Capital
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1 px-2 py-1 rounded-full border border-primary/20 bg-card/40 backdrop-blur-sm">
            {navLinks?.map((link) => (
              <Link
                key={link?.href}
                href={link?.href}
                className="px-4 py-2 rounded-full text-xs font-semibold tracking-widest uppercase text-muted-foreground hover:text-foreground hover:bg-primary/10 transition-all duration-200"
              >
                {link?.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              href="/contact"
              className="text-xs font-semibold tracking-widest uppercase text-muted-foreground hover:text-primary transition-colors duration-200"
            >
              Join Investor Network
            </Link>
            <Link
              href="/contact"
              className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold tracking-widest uppercase hover:bg-accent transition-all duration-200"
            >
              Raise Capital
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden flex flex-col justify-center items-center w-11 h-11 gap-1.5 rounded-lg border border-primary/20 bg-card/40 backdrop-blur-sm"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span
              className={`block w-5 h-0.5 bg-foreground transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}
            />
            <span
              className={`block w-5 h-0.5 bg-foreground transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`}
            />
            <span
              className={`block w-5 h-0.5 bg-foreground transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}
            />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 flex flex-col transition-all duration-400 md:hidden ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ background: 'rgba(11,15,26,0.97)', backdropFilter: 'blur(20px)' }}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-primary/10">
          <Link href="/" className="flex items-center gap-2.5" onClick={() => setMenuOpen(false)}>
            <AppLogo size={28} />
            <span className="font-sans text-sm font-semibold tracking-widest uppercase text-foreground">
              Aregn Capital
            </span>
          </Link>
          <button
            onClick={() => setMenuOpen(false)}
            className="w-11 h-11 flex items-center justify-center text-muted-foreground hover:text-foreground"
            aria-label="Close menu"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <nav className="flex flex-col px-6 pt-10 gap-2">
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              onClick={() => setMenuOpen(false)}
              className="py-4 text-lg font-semibold tracking-wide text-foreground border-b border-primary/10 hover:text-primary transition-colors"
            >
              {link?.label}
            </Link>
          ))}
        </nav>
        <div className="flex flex-col px-6 pt-8 gap-3">
          <Link
            href="/contact"
            onClick={() => setMenuOpen(false)}
            className="w-full py-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold tracking-widest uppercase text-center hover:bg-accent transition-all"
          >
            Raise Capital
          </Link>
          <Link
            href="/contact"
            onClick={() => setMenuOpen(false)}
            className="w-full py-4 rounded-full border border-primary/30 text-primary text-sm font-semibold tracking-widest uppercase text-center hover:bg-primary/10 transition-all"
          >
            Join Investor Network
          </Link>
        </div>
      </div>
    </>
  );
}
